/**
 * AddressSelector - Address dropdown with inline "Add Address" for visit creation
 * Shows customer addresses and allows adding new ones without leaving the flow
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, MapPin, CheckCircle } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AddressSelector({
  customerId,
  addresses = [],
  selectedAddressId,
  onAddressSelect,
  onAddressAdded,
  disabled = false,
}) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: "",
    street: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    is_primary: false,
  });

  const formatAddress = (addr) => {
    if (!addr) return "";
    const parts = [
      addr.street || addr.address_line1,
      addr.city,
      addr.state,
      addr.zip,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    if (!newAddress.street.trim() || !newAddress.city.trim() || !newAddress.state.trim() || !newAddress.zip.trim()) {
      toast.error("Street, city, state, and ZIP are required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/customers/${customerId}/addresses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newAddress),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Address added");
        onAddressAdded(data.addresses);
        onAddressSelect(data.address.id);
        setShowAddModal(false);
        setNewAddress({
          label: "",
          street: "",
          address_line2: "",
          city: "",
          state: "",
          zip: "",
          is_primary: false,
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add address");
      }
    } catch (error) {
      toast.error("Failed to add address");
    } finally {
      setLoading(false);
    }
  };

  // Find primary address for display
  const primaryAddress = addresses.find((a) => a.is_primary);
  const defaultAddressId = selectedAddressId || primaryAddress?.id || addresses[0]?.id;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1">
          <MapPin size={14} className="text-zinc-500" />
          Service Address
        </Label>
        {customerId && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-[#FF6600] hover:text-[#CC5200] hover:bg-orange-50"
            onClick={() => setShowAddModal(true)}
            disabled={disabled}
            data-testid="add-address-btn"
          >
            <Plus size={14} className="mr-1" />
            Add Address
          </Button>
        )}
      </div>

      {addresses.length > 0 ? (
        <Select
          value={defaultAddressId?.toString()}
          onValueChange={(v) => onAddressSelect(v)}
          disabled={disabled || !customerId}
        >
          <SelectTrigger className="h-11 border-2" data-testid="address-select">
            <SelectValue placeholder="Select address">
              {defaultAddressId && (
                <span className="text-sm">
                  {formatAddress(addresses.find((a) => a.id?.toString() === defaultAddressId?.toString()))}
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {addresses.map((addr) => (
              <SelectItem key={addr.id} value={addr.id?.toString()}>
                <div className="flex items-center gap-2">
                  {addr.is_primary && (
                    <CheckCircle size={14} className="text-green-600" weight="fill" />
                  )}
                  <span>
                    {addr.label && <span className="font-medium mr-1">{addr.label}:</span>}
                    {formatAddress(addr)}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="h-11 border-2 border-dashed border-zinc-300 rounded-md flex items-center justify-center text-sm text-zinc-500">
          {customerId ? "No addresses - add one above" : "Select a customer first"}
        </div>
      )}

      {/* Add Address Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-lg uppercase flex items-center gap-2">
              <MapPin size={18} weight="bold" />
              Add Address
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAddress} className="space-y-3 mt-3">
            <div>
              <Label htmlFor="addr-label">Label (optional)</Label>
              <Input
                id="addr-label"
                value={newAddress.label}
                onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                placeholder="Home, Work, Office..."
                className="mt-1 h-10 border-2"
                data-testid="new-address-label"
              />
            </div>
            <div>
              <Label htmlFor="addr-street">Street Address *</Label>
              <Input
                id="addr-street"
                value={newAddress.street}
                onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                placeholder="123 Main St"
                className="mt-1 h-10 border-2"
                required
                data-testid="new-address-street"
              />
            </div>
            <div>
              <Label htmlFor="addr-line2">Apt/Suite/Unit</Label>
              <Input
                id="addr-line2"
                value={newAddress.address_line2}
                onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })}
                placeholder="Apt 4B"
                className="mt-1 h-10 border-2"
                data-testid="new-address-line2"
              />
            </div>
            <div className="grid grid-cols-6 gap-2">
              <div className="col-span-3">
                <Label htmlFor="addr-city">City *</Label>
                <Input
                  id="addr-city"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                  placeholder="Boston"
                  className="mt-1 h-10 border-2"
                  required
                  data-testid="new-address-city"
                />
              </div>
              <div className="col-span-1">
                <Label htmlFor="addr-state">State *</Label>
                <Input
                  id="addr-state"
                  value={newAddress.state}
                  onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                  placeholder="MA"
                  maxLength={2}
                  className="mt-1 h-10 border-2 uppercase"
                  required
                  data-testid="new-address-state"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="addr-zip">ZIP *</Label>
                <Input
                  id="addr-zip"
                  value={newAddress.zip}
                  onChange={(e) => setNewAddress({ ...newAddress, zip: e.target.value })}
                  placeholder="02101"
                  maxLength={10}
                  className="mt-1 h-10 border-2"
                  required
                  data-testid="new-address-zip"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="addr-primary"
                checked={newAddress.is_primary}
                onChange={(e) => setNewAddress({ ...newAddress, is_primary: e.target.checked })}
                className="rounded border-zinc-300"
                data-testid="new-address-primary"
              />
              <Label htmlFor="addr-primary" className="text-sm font-normal cursor-pointer">
                Set as primary address
              </Label>
            </div>
            <div className="flex gap-2 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border-2"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                disabled={loading}
                data-testid="save-new-address"
              >
                {loading ? "Adding..." : "Add Address"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
